import { computeTotals } from '../../modules/docs/types/documents.types';
import { normalizeInvoiceRole } from '../../modules/docs/utils/invoiceLedger';
import { workspaceLifecycleStatus } from '../../utils/workflowUtils';
import { carryingInstallDays, groupCompletionsByCard, type CarryingCard, type JobCompletionRow } from '../project-workspace/openJobCards.model';

export type JobReviewStatus = 'to_review' | 'reviewed';
export type LabourUnit = 'day' | 'hour' | 'other';
export type ReviewBasis = 'quote' | 'invoice';
export type ReviewLineKind = 'labour' | 'material' | 'equipment' | 'other';

export const REVIEW_LINE_KIND_ORDER: ReviewLineKind[] = ['labour', 'material', 'equipment', 'other'];

export type ReviewProject = {
  id: string;
  project_name?: string | null;
  customer_name?: string | null;
  supply_only?: boolean | null;
  complete?: boolean | null;
  project_status?: 'active' | 'completed' | 'cancelled' | null;
  status?: string | null;
  install_date?: string | null;
  workflow_tasks?: Array<{ task: string; completed?: boolean }> | null;
};

export type ReviewQuote = {
  id: string;
  project_id: string;
  status?: string | null;
  lifecycle_ref?: string | null;
  invoice_ref?: string | null;
  accepted_at?: string | null;
  created_at?: string | null;
  active_version_id?: string | null;
  invoice_role?: string | null;
  admin_pct?: number | null;
  admin_cap?: number | null;
  contingency_pct?: number | null;
  profit_pct?: number | null;
  vat_enabled?: boolean | null;
  vat_pct?: number | null;
  gross_total?: number | null;
};

export type ReviewQuoteLine = {
  id?: string | null;
  document_id: string;
  name?: string | null;
  qty?: number | null;
  unit?: string | null;
  unit_price?: number | null;
  line_total?: number | null;
  sort_order?: number | null;
  line_kind?: string | null;
  vat_inclusive?: boolean | null;
};

export type ReviewLabourLine = {
  lineId: string | null;
  documentId?: string | null;
  name: string;
  unit: LabourUnit;
  quotedQty: number;
  rate: number;
  actualQty: number | null;
  /**
   * What was actually paid per unit; null means as sold. On a Company Flat Rate line the actual
   * qty and rate are the in-house fitting labour only (the company share stays as quoted).
   */
  actualRate?: number | null;
  lineKind: ReviewLineKind;
  vatInclusive?: boolean;
};

export type ReviewExtraCost = {
  id: string;
  description: string;
  amount: number;
};

/**
 * What one person who worked the job cost (net of VAT). Stored on the Job Record as
 * subcontractor_costs. `covered` = the flat rate's fitting labour pays for their day (Tom),
 * so they start at the fitting day rate; everyone else is entered from their bill.
 */
export type ReviewSubcontractorCost = {
  id: string;
  name: string;
  accountId?: string | null;
  profileId?: string | null;
  covered?: boolean;
  /** Days on the job cards, for reference. */
  days?: number;
  /** Days actually worked, when they differ from the job cards. */
  actualDays?: number | null;
  /** What they charge per day; days × day rate fills in the cost. */
  dayRate?: number | null;
  amount: number;
};

/** Days a person worked on the job: what was entered, else what the job cards say. */
export function workerDays(row: Pick<ReviewSubcontractorCost, 'actualDays' | 'days'>): number {
  return row.actualDays ?? row.days ?? 0;
}

/** What the quote priced for fitting against what the people who did the work cost. */
export function fittingComparison(
  lines: ReviewLabourLine[],
  components: RateComponents | null,
  workers: ReviewSubcontractorCost[],
): { pricedDays: number; pricedRate: number; pricedCost: number; actualDays: number; actualCost: number; over: number } {
  const pricedDays = round2(lines.filter((line) => isCompanyFlatRate(line.name)).reduce((sum, line) => sum + line.quotedQty, 0));
  const pricedCost = quotedFittingCost(lines, components);
  const actualCost = round2(workers.reduce((sum, row) => sum + (Number(row.amount) || 0), 0));
  return {
    pricedDays,
    pricedRate: fittingDayRate(lines, components),
    pricedCost,
    actualDays: round2(workers.reduce((sum, row) => sum + workerDays(row), 0)),
    actualCost,
    over: round2(actualCost - pricedCost),
  };
}

/** Someone on the workspace's job cards, with their days on site. */
export type ReviewWorkPerson = {
  id: string;
  name: string;
  accountId: string | null;
  profileId: string | null;
  days: number;
  covered: boolean;
};

/** What the Company Flat Rate was made of from a date (company_rate_components). */
export type RateComponents = {
  effectiveFrom: string;
  directorsPerDay: number;
  overheadPerDay: number;
  fittingPerDay: number;
  /** Sign-in email of the person whose day the fitting part pays for. */
  fittingEmail?: string | null;
};

/** One day of a flat rate split into its parts. */
export type FlatRateSplit = {
  directors: number;
  overhead: number;
  fitting: number;
};

export type JobReviewRecord = {
  id?: string;
  projectId: string;
  quoteDocumentId: string | null;
  status: JobReviewStatus;
  labourLines: ReviewLabourLine[];
  extraCosts: ReviewExtraCost[];
  subcontractorCosts: ReviewSubcontractorCost[];
  notes: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  updatedAt?: string | null;
};

export type ReviewEvent = {
  project_id: string;
  event_type?: string | null;
  event_date?: string | null;
  end_date?: string | null;
  status?: string | null;
};

export type ReviewSummary = {
  quotedDays: number;
  actualDays: number;
  quotedHours: number;
  actualHours: number;
  /** Fixed customer price before VAT. Actuals never change it. */
  soldExVat: number;
  /** Sold lines at sold quantities and rates, net of VAT. */
  quotedCost: number;
  /** Lines at actual quantities and rates, plus extras and subcontractors. */
  actualCost: number;
  extrasTotal: number;
  subcontractorTotal: number;
  /** Directors' and running-cost share of the flat rate: carried as quoted, whoever fitted. */
  companyShare: number;
  plannedProfit: number;
  actualProfit: number;
  /** Positive means the job cost less than quoted (actual profit above planned). */
  variance: number;
};

export type ReviewQueueRow = {
  projectId: string;
  jobRef: string;
  workspace: string;
  customer: string;
  status: JobReviewStatus;
  legacy: boolean;
  installedOn: string | null;
  daysWaiting: number | null;
  summary: ReviewSummary | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  installDone: boolean;
  hasQuotedLabour: boolean;
  basis: ReviewBasis;
  /** When progress was last saved, or null if no record has been started. */
  recordUpdatedAt: string | null;
};

const round2 = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

export function reviewLineKind(value?: string | null): ReviewLineKind {
  const next = String(value || '').toLowerCase();
  if (next === 'material' || next === 'equipment' || next === 'other') return next;
  return 'labour';
}

export function reviewLineKindLabel(kind: ReviewLineKind): string {
  if (kind === 'material') return 'Materials';
  if (kind === 'equipment') return 'Equipment';
  if (kind === 'other') return 'Additional';
  return 'Labour';
}

export function groupReviewLines(lines: ReviewLabourLine[]): Array<{
  kind: ReviewLineKind;
  label: string;
  lines: ReviewLabourLine[];
}> {
  return REVIEW_LINE_KIND_ORDER
    .map((kind) => ({
      kind,
      label: reviewLineKindLabel(kind),
      lines: lines.filter((line) => reviewLineKind(line.lineKind) === kind),
    }))
    .filter((group) => group.lines.length > 0);
}

export function labourUnit(unit?: string | null): LabourUnit {
  const text = String(unit || '').trim().toLowerCase();
  if (/^(h|hr|hrs|hour|hours)$/.test(text)) return 'hour';
  if (/^(d|day|days)$/.test(text)) return 'day';
  return 'other';
}

export function unitLabel(unit: LabourUnit, qty: number): string {
  if (unit === 'day') return qty === 1 ? 'day' : 'days';
  if (unit === 'hour') return qty === 1 ? 'hour' : 'hours';
  return qty === 1 ? 'unit' : 'units';
}

export function formatQty(value: number): string {
  return String(Math.round(value * 100) / 100);
}

export function formatMoney(value: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
}

/** "1 day over", "0.5 days under" or "No drift". */
export function qtyVarianceLabel(
  quoted: number,
  actual: number,
  unit: LabourUnit,
  _basis: ReviewBasis = 'quote',
): string {
  const diff = Math.round((actual - quoted) * 100) / 100;
  if (!diff) return 'No drift';
  const size = Math.abs(diff);
  return `${formatQty(size)} ${unitLabel(unit, size)} ${diff > 0 ? 'over' : 'under'}`;
}

/** "£120.00 under" / "£80.00 over" / "No drift", where positive variance means under the quote. */
export function moneyVarianceLabel(variance: number, _basis: ReviewBasis = 'quote'): string {
  if (!Math.round(variance * 100)) return 'No drift';
  return `${formatMoney(Math.abs(variance))} ${variance > 0 ? 'under' : 'over'}`;
}

export function varianceColor(variance: number): 'success' | 'danger' | 'neutral' {
  if (!Math.round(variance * 100)) return 'success';
  return variance > 0 ? 'success' : 'danger';
}

export function isAcceptedQuote(quote?: Pick<ReviewQuote, 'status'> | null): boolean {
  const status = String(quote?.status || '').toLowerCase();
  return status === 'accepted' || status === 'cash' || status === 'invoiced';
}

export const ACCEPTED_QUOTE_STATUSES = ['accepted', 'cash', 'invoiced'];

export function acceptedQuoteFor(projectId: string, quotes: ReviewQuote[]): ReviewQuote | null {
  const stamp = (quote: ReviewQuote) => Date.parse(quote.accepted_at || quote.created_at || '') || 0;
  return quotes
    .filter((quote) => quote.project_id === projectId && isAcceptedQuote(quote))
    .sort((a, b) => stamp(b) - stamp(a))[0] || null;
}

function sourceStamp(row: ReviewQuote): number {
  return Date.parse(row.accepted_at || row.created_at || '') || 0;
}

export function isInvoiceLabourSource(invoice?: Pick<ReviewQuote, 'status' | 'invoice_role'> | null): boolean {
  const status = String(invoice?.status || '').toLowerCase();
  if (status === 'void' || status === 'rejected') return false;
  const role = normalizeInvoiceRole(invoice?.invoice_role);
  return role !== 'deposit' && role !== 'credit';
}

export function labourInvoicesFor(projectId: string, invoices: ReviewQuote[]): ReviewQuote[] {
  return invoices
    .filter((invoice) => invoice.project_id === projectId && isInvoiceLabourSource(invoice))
    .sort((a, b) => sourceStamp(b) - sourceStamp(a));
}

export function sourceJobRef(source?: Pick<ReviewQuote, 'lifecycle_ref' | 'invoice_ref'> | null): string | null {
  const invoiceRef = String(source?.invoice_ref || '').trim();
  if (invoiceRef) return invoiceRef;
  const lifecycleRef = String(source?.lifecycle_ref || '').trim();
  return lifecycleRef || null;
}

export function selectReviewBasis(args: {
  projectId: string;
  quotes: ReviewQuote[];
  invoices?: ReviewQuote[];
}): { basis: ReviewBasis; source: ReviewQuote | null; sources: ReviewQuote[] } {
  const quote = acceptedQuoteFor(args.projectId, args.quotes);
  if (quote) return { basis: 'quote', source: quote, sources: [quote] };
  const invoices = labourInvoicesFor(args.projectId, args.invoices || []);
  return { basis: 'invoice', source: invoices[0] || null, sources: invoices };
}

/** Installation Complete (or Delivery Complete for supply-only) is ticked and the job is not cancelled. */
export function isInstallDone(project: ReviewProject): boolean {
  if (workspaceLifecycleStatus(project) === 'Cancelled') return false;
  const target = project.supply_only ? 'Delivery Complete' : 'Installation Complete';
  const tasks = Array.isArray(project.workflow_tasks) ? project.workflow_tasks : [];
  return tasks.some((task) => task.task === target && !!task.completed);
}

export const JOB_RECORD_BLOCKS_COMPLETE_MESSAGE =
  'Complete the Job Record on Aftercare before marking this workspace complete.';

export function canMarkReviewed(project: ReviewProject): boolean {
  return workspaceLifecycleStatus(project) !== 'Cancelled';
}

export function isJobRecordComplete(
  review: Pick<JobReviewRecord, 'status'> | null,
  hasLegacyActuals: boolean,
): boolean {
  return queueStatus(review, hasLegacyActuals) === 'reviewed';
}

/** Dashboard: an install or delivery date is booked, and the Job Record is not reviewed yet. */
export function isActualsReminderDue(row: Pick<ReviewQueueRow, 'status' | 'installedOn'>): boolean {
  return row.status !== 'reviewed' && !!row.installedOn;
}

/** Due once the booked install (or delivery) day has passed; today's or a future booking is not due yet. */
export function isActualsReminderDueOn(row: Pick<ReviewQueueRow, 'status' | 'installedOn'>, today: string): boolean {
  return isActualsReminderDue(row) && String(row.installedOn) < today;
}

export function queueStatus(review: Pick<JobReviewRecord, 'status'> | null, hasLegacyActuals: boolean): JobReviewStatus {
  if (review) return review.status;
  return hasLegacyActuals ? 'reviewed' : 'to_review';
}

/**
 * Quoted labour lines merged with any saved actuals. Saved lines match by quote line id,
 * then by name; saved lines whose quote line has gone are kept so nothing entered is lost.
 */
export function buildLabourLines(quoteLines: ReviewQuoteLine[], saved: ReviewLabourLine[] = []): ReviewLabourLine[] {
  const unused = [...saved];
  const take = (lineId: string | null, name: string) => {
    let index = lineId ? unused.findIndex((line) => line.lineId === lineId) : -1;
    if (index < 0) index = unused.findIndex((line) => line.name === name);
    return index < 0 ? null : unused.splice(index, 1)[0];
  };
  const fromQuote = [...quoteLines]
    .sort((a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0))
    .map((line) => {
      const lineId = line.id || null;
      const lineKind = reviewLineKind(line.line_kind);
      const name = String(line.name || '').trim() || reviewLineKindLabel(lineKind);
      const quotedQty = Number(line.qty || 0);
      const lineTotal = Number(line.line_total ?? quotedQty * Number(line.unit_price || 0));
      const rate = Number(line.unit_price || 0) || (quotedQty ? round2(lineTotal / quotedQty) : 0);
      const match = take(lineId, name);
      return {
        lineId,
        documentId: line.document_id || match?.documentId || null,
        name,
        unit: labourUnit(line.unit),
        quotedQty,
        rate,
        actualQty: match ? match.actualQty : null,
        actualRate: match?.actualRate ?? null,
        lineKind,
        vatInclusive: line.vat_inclusive != null ? !!line.vat_inclusive : !!match?.vatInclusive,
      };
    });
  return [
    ...fromQuote,
    ...unused.map((line) => ({
      ...line,
      lineKind: reviewLineKind(line.lineKind),
      vatInclusive: !!line.vatInclusive,
    })),
  ];
}

/** Starting values for a new review: actuals begin at the quoted amounts. */
export function prefillActuals(lines: ReviewLabourLine[]): ReviewLabourLine[] {
  return lines.map((line) => (line.actualQty == null ? { ...line, actualQty: line.quotedQty } : line));
}

export function isCompanyFlatRate(name?: string | null): boolean {
  const value = String(name || '').trim().toLowerCase();
  return /company\s*flat\s*rate/.test(value) || value === 'flat rate';
}

/** The split in force on a date: the latest row whose effective_from is on or before it. */
export function componentsOn(date: string | null | undefined, rows: RateComponents[]): RateComponents | null {
  const day = String(date || '').slice(0, 10);
  const sorted = [...rows].sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom));
  if (!sorted.length) return null;
  if (!day) return sorted[sorted.length - 1];
  const inForce = sorted.filter((row) => row.effectiveFrom <= day);
  return inForce.length ? inForce[inForce.length - 1] : sorted[0];
}

/**
 * One day at the quoted flat rate, split into parts. Fitting labour is a fixed day rate; whatever is
 * left is shared between directors and running overhead in the same proportions as the table
 * (so a quote typed at £620 instead of £650 still splits sensibly).
 */
export function splitFlatRate(rate: number, components: RateComponents): FlatRateSplit {
  const fitting = round2(Math.min(Math.max(components.fittingPerDay, 0), Math.max(rate, 0)));
  const rest = round2(Math.max(rate, 0) - fitting);
  const shareBase = components.directorsPerDay + components.overheadPerDay;
  const directors = shareBase > 0 ? round2(rest * (components.directorsPerDay / shareBase)) : 0;
  return { directors, overhead: round2(rest - directors), fitting };
}

function totalsConfigFrom(source?: ReviewQuote | null) {
  return {
    adminPct: Number(source?.admin_pct ?? 0),
    adminCap: Number(source?.admin_cap ?? 0),
    contingencyPct: Number(source?.contingency_pct ?? 0),
    profitPct: Number(source?.profit_pct ?? 0),
    vatEnabled: source?.vat_enabled !== false,
    vatPct: Number(source?.vat_pct ?? 0.2),
  };
}

function projectValue(
  lines: ReviewLabourLine[],
  qtyOf: (line: ReviewLabourLine) => number,
  source?: ReviewQuote | null,
  measure: 'gross' | 'exVat' = 'gross',
): number {
  const config = totalsConfigFrom(source);
  const totals = computeTotals(
    lines.map((line) => {
      const qty = qtyOf(line);
      return {
        lineKind: line.lineKind,
        qty,
        unitPrice: line.rate,
        lineTotal: round2(qty * line.rate),
        vatInclusive: !!line.vatInclusive,
      };
    }),
    {
      adminPct: config.adminPct,
      adminCap: config.adminCap,
      contingencyPct: config.contingencyPct,
      profitPct: config.profitPct,
    },
    { enabled: config.vatEnabled, pct: config.vatPct },
  );
  return measure === 'exVat' ? round2(totals.preProfit + totals.profit) : totals.grandTotal;
}

function projectValueForSources(
  lines: ReviewLabourLine[],
  qtyOf: (line: ReviewLabourLine) => number,
  sources: ReviewQuote[],
  measure: 'gross' | 'exVat' = 'gross',
): number {
  if (sources.length <= 1) return projectValue(lines, qtyOf, sources[0] || null, measure);
  const leftover = [...lines];
  const total = sources.reduce((sum, source) => {
    const owned = leftover.filter((line) => line.documentId === source.id);
    owned.forEach((line) => leftover.splice(leftover.indexOf(line), 1));
    return sum + projectValue(owned, qtyOf, source, measure);
  }, 0);
  return round2(total + projectValue(leftover, qtyOf, sources[0] || null, measure));
}

function sourceFor(line: ReviewLabourLine, sources: ReviewQuote[]): ReviewQuote | null {
  return sources.find((source) => source.id === line.documentId) || sources[0] || null;
}

/** Net of VAT, matching the quote engine line by line. */
function netAmount(gross: number, line: ReviewLabourLine, source: ReviewQuote | null): number {
  const config = totalsConfigFrom(source);
  if (line.vatInclusive && config.vatEnabled) return round2(gross / (1 + config.vatPct));
  return round2(gross);
}

export type ReviewLineCost = {
  quoted: number;
  actual: number;
  /** Flat rate lines only: the split of one sold day, and the company share carried as quoted. */
  split: FlatRateSplit | null;
  companyShare: number;
};

/**
 * Quoted and actual cost of one line, net of VAT. With a split, a Company Flat Rate line keeps its
 * directors' and overhead share at the sold days; actual qty × rate is the in-house fitting only.
 */
export function reviewLineCost(
  line: ReviewLabourLine,
  sources: ReviewQuote[] = [],
  components: RateComponents | null = null,
  /** Fitting is costed per person under Who did the work, so the line carries the company share only. */
  fittingFromWorkers = false,
): ReviewLineCost {
  const source = sourceFor(line, sources);
  const actualQty = line.actualQty ?? line.quotedQty;
  const quoted = netAmount(line.quotedQty * line.rate, line, source);
  if (components && isCompanyFlatRate(line.name)) {
    const split = splitFlatRate(line.rate, components);
    const companyShare = netAmount(line.quotedQty * (split.directors + split.overhead), line, source);
    if (fittingFromWorkers) return { quoted, actual: companyShare, split, companyShare };
    const fittingRate = line.actualRate ?? split.fitting;
    const actual = round2(companyShare + netAmount(actualQty * fittingRate, line, source));
    return { quoted, actual, split, companyShare };
  }
  const actual = netAmount(actualQty * (line.actualRate ?? line.rate), line, source);
  return { quoted, actual, split: null, companyShare: 0 };
}

/** The fitting day rate in the flat rate (Tom's day), from the first flat rate line's split. */
export function fittingDayRate(lines: ReviewLabourLine[], components: RateComponents | null): number {
  if (!components) return 0;
  const flat = lines.find((line) => isCompanyFlatRate(line.name));
  return flat ? splitFlatRate(flat.rate, components).fitting : components.fittingPerDay;
}

/** Fitting labour the quote allowed: fitting share × sold days across the flat rate lines. */
export function quotedFittingCost(lines: ReviewLabourLine[], components: RateComponents | null): number {
  if (!components) return 0;
  return round2(lines
    .filter((line) => isCompanyFlatRate(line.name))
    .reduce((sum, line) => sum + line.quotedQty * splitFlatRate(line.rate, components).fitting, 0));
}

/**
 * One cost row per person on the job cards, plus anyone added by hand. Saved amounts win;
 * otherwise the person the flat rate covers starts at days × fitting day rate, everyone else at 0.
 */
export function buildWorkerCosts(
  people: ReviewWorkPerson[],
  saved: ReviewSubcontractorCost[],
  dayRate: number,
): ReviewSubcontractorCost[] {
  const unused = [...saved];
  const take = (person: ReviewWorkPerson) => {
    const index = unused.findIndex((row) => row.id === person.id
      || (!!person.accountId && row.accountId === person.accountId)
      || (!!person.profileId && row.profileId === person.profileId));
    return index < 0 ? null : unused.splice(index, 1)[0];
  };
  const fromCards = people.map((person) => {
    const match = take(person);
    const savedRate = match?.dayRate != null ? Number(match.dayRate) || 0 : null;
    return {
      id: person.id,
      name: person.name,
      accountId: person.accountId,
      profileId: person.profileId,
      covered: person.covered,
      days: person.days,
      actualDays: match?.actualDays ?? null,
      dayRate: savedRate ?? (person.covered && dayRate > 0 ? dayRate : null),
      amount: match ? Number(match.amount) || 0 : person.covered ? round2(person.days * dayRate) : 0,
    };
  });
  return [...fromCards, ...unused];
}

/** Sold is the fixed customer price before VAT; profit is that price less what the job actually cost. */
export function summariseReview(
  lines: ReviewLabourLine[],
  extras: ReviewExtraCost[] = [],
  sources: ReviewQuote[] = [],
  options: {
    subcontractors?: ReviewSubcontractorCost[];
    components?: RateComponents | null;
    /** Cost fitting from the people who did the work instead of the flat rate line's own qty. */
    fittingFromWorkers?: boolean;
  } = {},
): ReviewSummary {
  const components = options.components ?? null;
  const fittingFromWorkers = !!options.fittingFromWorkers && !!components;
  const summary = lines.reduce((acc, line) => {
    const actualQty = line.actualQty ?? line.quotedQty;
    if (line.unit === 'day') {
      acc.quotedDays += line.quotedQty;
      acc.actualDays += actualQty;
    }
    if (line.unit === 'hour') {
      acc.quotedHours += line.quotedQty;
      acc.actualHours += actualQty;
    }
    const cost = reviewLineCost(line, sources, components, fittingFromWorkers);
    acc.quotedCost += cost.quoted;
    acc.actualCost += cost.actual;
    acc.companyShare += cost.companyShare;
    return acc;
  }, { quotedDays: 0, actualDays: 0, quotedHours: 0, actualHours: 0, quotedCost: 0, actualCost: 0, companyShare: 0 });
  const extrasTotal = round2(extras.reduce((sum, extra) => sum + (Number(extra.amount) || 0), 0));
  const subcontractorTotal = round2((options.subcontractors || []).reduce((sum, sub) => sum + (Number(sub.amount) || 0), 0));
  const soldExVat = projectValueForSources(lines, (line) => line.quotedQty, sources, 'exVat');
  const quotedCost = round2(summary.quotedCost);
  const actualCost = round2(summary.actualCost + extrasTotal + subcontractorTotal);
  const plannedProfit = round2(soldExVat - quotedCost);
  const actualProfit = round2(soldExVat - actualCost);
  return {
    quotedDays: round2(summary.quotedDays),
    actualDays: round2(summary.actualDays),
    quotedHours: round2(summary.quotedHours),
    actualHours: round2(summary.actualHours),
    soldExVat,
    quotedCost,
    actualCost,
    extrasTotal,
    subcontractorTotal,
    companyShare: round2(summary.companyShare),
    plannedProfit,
    actualProfit,
    variance: round2(actualProfit - plannedProfit),
  };
}

function toDateOnly(value?: string | null): string | null {
  const match = String(value || '').match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : null;
}

export type ReviewJobCard = CarryingCard & { project_id: string };

/**
 * Last on-site day, so multi-day installs count from when they finished. With job cards, only the
 * cards still carrying the job count: an ended card's date drops out until the follow-on card is dated.
 */
export function installedOn(
  project: ReviewProject,
  events: ReviewEvent[],
  jobCards: { cards: ReviewJobCard[]; completionsByCard: Map<string, JobCompletionRow[]> } = { cards: [], completionsByCard: new Map() },
): string | null {
  const type = project.supply_only ? 'deliveryDateConfirmation' : 'installationDateConfirmation';
  const bookings = events
    .filter((event) => event.project_id === project.id && event.event_type === type)
    .filter((event) => String(event.status || '').toLowerCase() !== 'cancelled');
  const dates = bookings
    .map((event) => toDateOnly(event.end_date || event.event_date))
    .filter((date): date is string => !!date)
    .sort();
  const calendarDay = dates[dates.length - 1] || toDateOnly(project.install_date);
  const cards = jobCards.cards.filter((card) => card.project_id === project.id);
  if (!cards.length) return calendarDay;
  const booking = bookings.reduce<ReviewEvent | null>((latest, event) => (
    !latest || String(event.event_date || '') >= String(latest.event_date || '') ? event : latest
  ), null);
  const days = carryingInstallDays(cards, jobCards.completionsByCard, booking);
  if (days.length) return days[days.length - 1];
  // A finished job keeps its booked date so its wait still counts
  return workspaceLifecycleStatus(project) === 'Completed' ? calendarDay : null;
}

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000);
}

/** The date a quote was priced on: when it was raised (accepted date as a fallback). */
export function quoteDateOf(source?: Pick<ReviewQuote, 'accepted_at' | 'created_at'> | null): string | null {
  return String(source?.created_at || source?.accepted_at || '').slice(0, 10) || null;
}

export function assembleReviewQueue(input: {
  projects: ReviewProject[];
  quotes: ReviewQuote[];
  quoteLines: ReviewQuoteLine[];
  invoices?: ReviewQuote[];
  invoiceLines?: ReviewQuoteLine[];
  reviews: JobReviewRecord[];
  legacyProjectIds: Set<string>;
  events: ReviewEvent[];
  jobCards?: ReviewJobCard[];
  completions?: JobCompletionRow[];
  reviewerNames?: Map<string, string>;
  rateComponents?: RateComponents[];
  today: string;
}): { inProgress: ReviewQueueRow[]; toReview: ReviewQueueRow[]; reviewed: ReviewQueueRow[] } {
  const reviewByProject = new Map(input.reviews.map((review) => [review.projectId, review]));
  const cardsByProject = new Map<string, ReviewJobCard[]>();
  (input.jobCards || []).forEach((card) => {
    cardsByProject.set(card.project_id, [...(cardsByProject.get(card.project_id) || []), card]);
  });
  const completionsByCard = groupCompletionsByCard(input.completions || []);
  const linesByDocument = new Map<string, ReviewQuoteLine[]>();
  [...input.quoteLines, ...(input.invoiceLines || [])].forEach((line) => {
    const list = linesByDocument.get(line.document_id) || [];
    list.push(line);
    linesByDocument.set(line.document_id, list);
  });

  const inProgress: ReviewQueueRow[] = [];
  const toReview: ReviewQueueRow[] = [];
  const reviewed: ReviewQueueRow[] = [];
  input.projects.forEach((project) => {
    const lifecycle = workspaceLifecycleStatus(project);
    if (lifecycle === 'Cancelled') return;
    const review = reviewByProject.get(project.id) || null;
    const legacy = input.legacyProjectIds.has(project.id);
    const selected = selectReviewBasis({
      projectId: project.id,
      quotes: input.quotes,
      invoices: input.invoices,
    });
    const status = queueStatus(review, legacy);
    const basisLines = selected.sources.flatMap((source) => linesByDocument.get(source.id) || []);
    let bucket: ReviewQueueRow[];
    if (status === 'reviewed') bucket = reviewed;
    else if (lifecycle === 'Completed') {
      if (!basisLines.length && !review) return;
      bucket = toReview;
    } else {
      if (!selected.source) return;
      bucket = inProgress;
    }

    const lines = buildLabourLines(basisLines, review?.labourLines || []);
    const onDate = installedOn(project, input.events, { cards: cardsByProject.get(project.id) || [], completionsByCard });
    const row: ReviewQueueRow = {
      projectId: project.id,
      jobRef: sourceJobRef(selected.source) || '—',
      workspace: project.project_name || 'Untitled',
      customer: project.customer_name || '—',
      status,
      legacy: legacy && !review,
      installedOn: onDate,
      daysWaiting: onDate ? Math.max(0, daysBetween(onDate, input.today)) : null,
      summary: review || !legacy
        ? summariseReview(lines, review?.extraCosts || [], selected.sources, {
          subcontractors: review?.subcontractorCosts || [],
          components: componentsOn(quoteDateOf(selected.source), input.rateComponents || []),
          fittingFromWorkers: (review?.subcontractorCosts || []).length > 0,
        })
        : null,
      reviewedAt: review?.reviewedAt || null,
      reviewedBy: review?.reviewedBy ? input.reviewerNames?.get(review.reviewedBy) || null : null,
      installDone: isInstallDone(project),
      hasQuotedLabour: basisLines.length > 0,
      basis: selected.source ? selected.basis : 'quote',
      recordUpdatedAt: review ? review.updatedAt || null : null,
    };
    bucket.push(row);
  });

  inProgress.sort((a, b) => (
    Number(b.installDone) - Number(a.installDone)
    || (a.installedOn || '9999').localeCompare(b.installedOn || '9999')
    || a.workspace.localeCompare(b.workspace)
  ));
  toReview.sort((a, b) => (b.daysWaiting ?? -1) - (a.daysWaiting ?? -1) || a.workspace.localeCompare(b.workspace));
  reviewed.sort((a, b) => String(b.reviewedAt || '').localeCompare(String(a.reviewedAt || '')) || a.workspace.localeCompare(b.workspace));
  return { inProgress, toReview, reviewed };
}
